/**
 * 사주 정보 입력 카드 (DEV_SPEC 3장 「사주 정보 입력」, 프로토타입 isBirthOpen).
 * 카운터는 장부 · 생일 시트(LedgerBirthSheet), 나 탭·1:1 에서는 사주 시트(BirthSheet)에 띄웁니다.
 *
 * - 양력/음력, 음력이면 윤달, 생년월일(연 → 월 → 일 고르기 시트, 귀인도감 방식), 태어난 시간(오전/오후 · 시 · 분 다이얼) 또는 「시간은 몰라요」
 * - 시간 모름을 고르면 시간 칸이 꺼지고 시간 없이 계산합니다 (DEV_SPEC 4.1 수용 기준 6)
 * - 윤달은 프로토타입에 없지만 음력 윤달생의 사주가 달라져 둡니다 (v1.0 입회에서 옮김)
 * - 성별(선택): 여 · 남 · 말 안 할래요. 기본 선택 없음. 큰 흐름(대운) 볼 때만 씁니다 (D25, DESIGN_SPEC 6장)
 */
import { useState } from 'react';
import { Button } from '../Button.jsx';
import { SegmentedControl } from '../SegmentedControl.jsx';
import { BirthDateField } from './BirthDatePicker.jsx';
import { BirthTimeField } from './BirthTimePicker.jsx';
import { EMPTY_BIRTH, isBirthComplete, toBirthPayload } from './birthUtils.js';

const CALENDAR_OPTIONS = [
    { value: 'solar', label: '양력' },
    { value: 'lunar', label: '음력' },
];

const GENDER_OPTIONS = [
    { value: 'female', label: '여' },
    { value: 'male', label: '남' },
    { value: 'none', label: '말 안 할래요' },
];

export function BirthForm({
    initialBirth = null,
    submitLabel = '이모한테 알려주기',
    busy = false,
    error = null,
    // 위에 같은 안내를 두는 시트(카운터 장부 · 생일)는 끔
    note = true,
    onSubmit,
}) {
    const [birth, setBirth] = useState(() => ({
        ...EMPTY_BIRTH,
        ...initialBirth,
        birthTime: initialBirth?.birthTime ?? '',
        // 저장된 값이 없으면 「말 안 할래요」로 다시 고르게 하지 않고 비워 둡니다
        gender: initialBirth?.gender ?? null,
    }));

    function update(patch) {
        setBirth((previous) => ({ ...previous, ...patch }));
    }

    return (
        <form
            aria-label="사주 정보"
            className="flex flex-col gap-4"
            onSubmit={(event) => {
                event.preventDefault();
                if (isBirthComplete(birth) && !busy) {
                    onSubmit(toBirthPayload(birth));
                }
            }}
        >
            {/* 입력이 곧 선택 동의(content/legal/consent_screen.md, DEV-07r 5 · D97) */}
            {note ? (
                <p className="m-0 text-[12px] leading-[18px] text-text-secondary">
                    풀이에만 쓰고, 나 탭에서 언제든 고치거나 지울 수 있어요.
                </p>
            ) : null}
            <SegmentedControl
                size="md"
                ariaLabel="달력 종류"
                options={CALENDAR_OPTIONS}
                value={birth.calendarType}
                onChange={(calendarType) => update({ calendarType })}
            />
            {/* 귀인도감 방식: 연 → 월 → 일을 펼쳐 놓고 눌러 고름(D78) */}
            <BirthDateField
                value={birth.birthDate}
                calendarType={birth.calendarType}
                onChange={(birthDate) => update({ birthDate })}
            />
            {birth.calendarType === 'lunar' ? (
                <CheckRow
                    label="윤달이에요"
                    checked={birth.isLeapMonth}
                    onChange={(isLeapMonth) => update({ isLeapMonth })}
                />
            ) : null}
            {/* 귀인도감 방식: 오전/오후 · 시 · 분 다이얼 + 시주 미리 보기(양력일 때 균시차까지, D79) */}
            <BirthTimeField
                value={birth.timeUnknown ? '' : birth.birthTime}
                disabled={birth.timeUnknown}
                birthDate={birth.calendarType === 'solar' ? birth.birthDate || null : null}
                onChange={(birthTime) => update({ birthTime })}
            />
            <CheckRow
                label="시간은 몰라요"
                checked={birth.timeUnknown}
                onChange={(timeUnknown) => update({ timeUnknown })}
            />
            <div className="flex flex-col gap-1.5">
                <span className="flex items-baseline gap-2">
                    <span className="text-label">성별</span>
                    <span className="text-caption text-text-secondary">큰 흐름 볼 때만 써요</span>
                </span>
                <SegmentedControl
                    size="md"
                    ariaLabel="성별"
                    options={GENDER_OPTIONS}
                    value={birth.gender}
                    onChange={(gender) => update({ gender })}
                />
            </div>
            {error ? (
                <p role="alert" className="m-0 text-caption text-error">
                    {error}
                </p>
            ) : null}
            <Button type="submit" fullWidth size="md" busy={busy} disabled={!isBirthComplete(birth)}>
                {submitLabel}
            </Button>
        </form>
    );
}

function CheckRow({ label, checked, onChange }) {
    return (
        <label className="flex min-h-touch cursor-pointer items-center gap-2.5 text-[15px]">
            <input
                type="checkbox"
                checked={checked}
                onChange={(event) => onChange(event.target.checked)}
                className="size-5 accent-brand"
            />
            {label}
        </label>
    );
}
